import React, { useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  Shield, ClipboardList, Server, Lock, Search, FileCheck,
  AlertCircle, ArrowLeft, ArrowRight, CheckCircle2, Activity,
  AlertTriangle, UserCog, FileSpreadsheet, Database, Building2,
  MessageCircle, Save, RotateCcw, Download, ChevronRight, ShieldCheck,
  LogOut, Settings, User, LayoutDashboard, KeyRound, ArrowUpRight, Landmark,
  Globe, Home, Layers, UserCheck
} from 'lucide-react';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';

// Import lib functions
import { calculateAssessmentScores, getDomainMaturityBand, getAnswerScore, SECTION_GROUPS, ENHANCED_SCORE_BANDS, buildStackAnalysis } from '@/lib/scoring-engine';
import { 
  getStackRecommendation, 
  isGreenfieldCandidate, 
  isManagedUpliftCandidate, 
  determineServiceTier,
  getServiceTierInfo,
  determineEnvironmentType,
  getEnvironmentTypeInfo
} from '@/lib/stack-mapper';
import { generateReport, getReportFilename, generatePriorityGaps } from '@/lib/report-generator';
import { computeEvidenceReadiness } from '@/lib/evidence-readiness';
import { downloadAsJSON } from '@/lib/storage';
import { SECTION_CONFIG, DOMAIN_WEIGHTS, COMPONENT_LABELS, LAYER_LABELS } from '@/config/domain-weights';
import { QUESTION_CONFIG, SCORED_SECTIONS } from '@/config/question-config';

// Import sub-components
import FieldRenderer from '@/components/FieldRenderer';

// Icon mapping
const iconMap = {
  FileText: ClipboardList,
  Building2: Building2,
  KeyRound: Lock,
  Monitor: Server,
  Shield: Shield,
  RefreshCw: Server,
  UserCog: UserCog,
  FileSpreadsheet: FileSpreadsheet,
  Database: Database,
  Activity: Activity,
  AlertTriangle: Search,
  ClipboardCheck: FileCheck,
  MessageCircle: MessageCircle,
  ShieldCheck: ShieldCheck,
  Landmark: Landmark,
  Globe: Globe,
  Home: Home,
  UserCheck: UserCheck,
  Layers3: Layers
};

// Status options for status questions
const statusOptions = [
  { value: 'yes', label: 'Yes / Fully implemented', score: 2 },
  { value: 'partial', label: 'Partially implemented', score: 1 },
  { value: 'no', label: 'No / Not implemented', score: 0 },
  { value: 'unknown', label: 'Unknown', score: 0 },
  { value: 'not_applicable', label: 'Not applicable', score: 2 }
];

// Helper functions
const getMaturityLabel = (score) => {
  if (score >= 85) return 'Strong';
  if (score >= 65) return 'Moderate';
  if (score >= 40) return 'Developing';
  return 'Poor';
};

const prettyStatus = (status) => {
  const labels = {
    not_ready: 'Not Ready',
    partially_ready: 'Partially Ready',
    substantially_ready: 'Substantially Ready',
    ready_for_validation: 'Ready for Validation'
  };
  return labels[status] || status;
};

// Section Score Pill Component
const SectionScorePill = ({ score }) => {
  const tone = score >= 85 
    ? 'bg-green-100 text-green-700' 
    : score >= 65 
    ? 'bg-blue-100 text-blue-700' 
    : score >= 40 
    ? 'bg-amber-100 text-amber-700' 
    : 'bg-red-100 text-red-700';
  
  return (
    <Badge className={cn('rounded-full px-3 py-1 text-xs font-medium', tone)}>
      {score}% {getMaturityLabel(score)}
    </Badge>
  );
};

// Main Component
export default function DiscoveryWorkbench({
  sections,
  currentStep,
  engagement,
  answers,
  submitted,
  isSaving,
  onUpdateSectionField,
  onUpdateEngagementField,
  onGoNext,
  onGoBack,
  onGoToSection,
  onGoToSectionById,
  onSubmit,
  onSave,
  onReset,
  onViewReport,
  // Pre-fill props
  prefilledIds,
  prefillSourceDate,
  prefillReviewed,
  onSetPrefillReviewed,
  // Auth props
  user,
  isAdmin,
  onLogout,
  onOpenAdmin,
  onBackToDashboard
}) {
  const [showChangePassword, setShowChangePassword] = useState(false);
  const currentSection = sections[currentStep];
  const totalSteps = sections.length;
  
  // Calculate progress based on answered questions (not section navigation)
  const progress = useMemo(() => {
    let totalQuestions = 0;
    let answeredQuestions = 0;
    
    sections.forEach(section => {
      // Skip engagement section for progress calculation (handled separately)
      if (section.id === 'engagement') {
        // Count engagement required fields
        const requiredEngagementFields = ['client_name', 'assessor_name', 'engagement_type'];
        totalQuestions += requiredEngagementFields.length;
        answeredQuestions += requiredEngagementFields.filter(f => engagement[f]).length;
        return;
      }
      
      section.questions?.forEach(question => {
        // Skip optional questions
        if (question.required === false) return;
        
        // Skip conditional questions if condition isn't met
        if (question.conditionalOn) {
          const { field, value: conditionValue } = question.conditionalOn;
          const parentAnswer = answers[field];
          const parentValue = parentAnswer?.answer || parentAnswer?.status;
          if (parentValue !== conditionValue) return;
        }
        
        totalQuestions++;
        
        const answer = answers[question.field_id];
        if (!answer) return;
        
        // For multiselect with "Other" selected, check if valid
        if (question.type === 'multiselect' && answer.isValid === false) {
          return; // Don't count as answered
        }
        
        if (answer.status) {
          answeredQuestions++;
        } else if (answer.answer !== undefined && answer.answer !== null && answer.answer !== '') {
          if (Array.isArray(answer.answer)) {
            if (answer.answer.length > 0) answeredQuestions++;
          } else {
            answeredQuestions++;
          }
        } else if (answer.value !== undefined && answer.value !== null && answer.value !== '') {
          if (Array.isArray(answer.value)) {
            if (answer.value.length > 0) answeredQuestions++;
          } else {
            answeredQuestions++;
          }
        }
      });
    });
    
    return totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;
  }, [sections, answers, engagement]);

  // Calculate results using scoring engine
  const results = useMemo(() => {
    const scoringResults = calculateAssessmentScores(answers);
    const { domainScores, overallScore, overallStatus, gatingStatus } = scoringResults;
    
    // Get stack recommendations
    const stackRecommendation = getStackRecommendation(answers);
    
    // Get classification
    const greenfieldCandidate = isGreenfieldCandidate(domainScores, answers);
    const managedUpliftCandidate = isManagedUpliftCandidate(overallScore, answers);
    const serviceTier = determineServiceTier(overallScore, greenfieldCandidate, managedUpliftCandidate, answers);
    
    // Get Environment Type (descriptive - separate from Service Tier)
    const environmentType = determineEnvironmentType(overallScore, domainScores);
    const environmentTypeInfo = getEnvironmentTypeInfo(environmentType);
    
    // Get Service Tier info
    const serviceTierInfo = getServiceTierInfo(serviceTier);
    
    // Get priority gaps
    const priorityGaps = generatePriorityGaps(answers);
    
    // Get top weak domains
    const topDomains = Object.entries(domainScores)
      .sort((a, b) => (a[1].percentage || 0) - (b[1].percentage || 0))
      .slice(0, 3)
      .map(([id]) => SECTION_CONFIG[id]?.title)
      .filter(Boolean);

    // Get gating failures for quick navigation
    const gatingFailures = gatingStatus.controls.filter(c => c.score === 0);
    const gatingPartials = gatingStatus.controls.filter(c => c.score === 1);

    // Top 5 discussion points
    const discussionPoints = priorityGaps.slice(0, 5).map(gap => ({
      domain: SECTION_CONFIG[gap.domain]?.title || gap.domain,
      sectionId: gap.domain,
      fieldId: gap.field_id,
      topic: gap.gap,
      priority: gap.priority
    }));

    // Missing stack layers
    const missingLayers = [];
    if (stackRecommendation.byLayer.enforcement_layer?.components.length > 0) {
      missingLayers.push('Enforcement');
    }
    if (stackRecommendation.byLayer.validation_layer?.components.length > 0) {
      missingLayers.push('Validation');
    }
    if (stackRecommendation.byLayer.monitoring_layer?.components.length > 0) {
      missingLayers.push('Monitoring');
    }
    if (stackRecommendation.byLayer.compliance_layer?.components.length > 0) {
      missingLayers.push('Compliance');
    }

    // Stack analysis (Technology Stack Presence & Maturity)
    const stackAnalysis = buildStackAnalysis(answers, priorityGaps);

    // Evidence Readiness (informational-only, independent of ML2/CES/PGS/CMS)
    const evidenceReadiness = computeEvidenceReadiness(answers);

    return {
      domainScores,
      domainScorePercentages: Object.fromEntries(
        Object.entries(domainScores).map(([k, v]) => [k, v.percentage || 0])
      ),
      overallScore,
      overallStatus,
      gatingFailures,
      gatingPartials,
      gatingStatus,
      fociGate: scoringResults.fociGate,
      priorityGaps,
      stackByLayer: stackRecommendation.byLayer,
      recommendedComponents: stackRecommendation.components,
      greenfieldCandidate,
      managedUpliftCandidate,
      recommendedServiceTier: serviceTier,
      serviceTierInfo,
      topDomains,
      environmentType,
      environmentTypeInfo,
      discussionPoints,
      missingLayers,
      enhanced: scoringResults.enhanced,
      stackAnalysis,
      evidenceReadiness
    };
  }, [answers]);

  // Check if all sections are completed
  const allSectionsCompleted = useMemo(() => {
    return sections.every(section => {
      const sectionQuestions = section.questions || [];
      
      if (section.id === 'engagement') {
        // Check engagement fields
        const requiredEngagementFields = ['client_name', 'assessor_name', 'engagement_type'];
        return requiredEngagementFields.every(f => engagement[f]);
      }
      
      // For other sections, check required questions
      const applicableQuestions = sectionQuestions.filter(q => {
        // Skip optional questions
        if (q.required === false) return false;
        
        // Skip conditional questions if condition isn't met
        if (q.conditionalOn) {
          const { field, value: conditionValue } = q.conditionalOn;
          const parentAnswer = answers[field];
          const parentValue = parentAnswer?.answer || parentAnswer?.status;
          return parentValue === conditionValue;
        }
        return true;
      });
      
      if (applicableQuestions.length === 0) return true;
      
      return applicableQuestions.every(q => {
        const answer = answers[q.field_id];
        if (!answer) return false;
        
        // For multiselect with "Other" selected, check if valid
        if (q.type === 'multiselect' && answer.isValid === false) {
          return false;
        }
        
        if (answer.status) return true;
        if (answer.answer !== undefined && answer.answer !== null && answer.answer !== '') {
          if (Array.isArray(answer.answer)) return answer.answer.length > 0;
          return true;
        }
        if (answer.value !== undefined && answer.value !== null && answer.value !== '') {
          if (Array.isArray(answer.value)) return answer.value.length > 0;
          return true;
        }
        return false;
      });
    });
  }, [sections, engagement, answers]);

  // Get the icon for current section
  const Icon = iconMap[currentSection?.icon] || Shield;

  // Check if current section is engagement (special handling)
  const isEngagementSection = currentSection?.id === 'engagement';

  // Handle export
  const handleExport = () => {
    const report = generateReport(engagement, answers);
    const filename = getReportFilename(engagement);
    downloadAsJSON(report, filename);
  };

  const handleExportAccelerator = () => {
    // Extract DISP scope answers (redistributed from old disp_context)
    const dispFieldIds = [
      'disp_01_defence_contract', 'disp_02_contract_requires_disp',
      'disp_03_classification_level', 'disp_06_physical_facility',
      'disp_07_facility_access_control', 'disp_08_hosting_model',
      'disp_09_ict_accreditation', 'disp_10_policy_documentation',
      'disp_11_evidence_readiness', 'disp_12_contract_scope_type',
      'disp_13_multiple_locations',
    ];
    const dispAnswers = {};
    dispFieldIds.forEach(id => {
      dispAnswers[id] = answers[id]?.answer ?? '';
    });

    // Extract FOCI answers
    const fociFieldIds = [
      'foci_01_foreign_ownership', 'foci_02_foreign_influence',
      'foci_03_foreign_entities_20', 'foci_04_foreign_entities_40',
      'foci_05_foreign_control', 'foci_06_foreign_arrangements',
      'foci_07_complexity',
    ];
    const fociAnswers = {};
    fociFieldIds.forEach(id => {
      fociAnswers[id] = answers[id]?.answer ?? '';
    });

    const fociRiskLevel = results.fociGate?.status === 'fail' ? 'high'
      : results.fociGate?.status === 'review_required' ? 'medium' : 'low';

    const payload = {
      meta: {
        source: 'DefencIQ Discovery',
        version: '2.0',
        exported_at: new Date().toISOString(),
      },
      client: {
        client_name: engagement.client_name,
        engagement_type: engagement.engagement_type,
        assessor: engagement.assessor_name,
        assessment_date: engagement.assessment_date,
      },
      discovery: {
        answers,
        results,
      },
      disp_context: {
        source: 'discovery_extended',
        answers: dispAnswers,
      },
      foci: {
        source: 'discovery',
        answers: fociAnswers,
        gate_status: results.fociGate?.status || 'pass',
        risk_level: fociRiskLevel,
      },
      csq_prefill: {},
      gaps: [],
      evidence: [],
    };

    const slug = (engagement.client_name || 'unknown').toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const filename = `defenciq-accelerator-export-${slug}-${ts}.json`;

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <>
    <div className="min-h-screen bg-slate-50 p-4 md:p-6 lg:p-8" data-testid="discovery-workbench">
      <div className="mx-auto grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)_380px] xl:grid-cols-[340px_minmax(0,1fr)_400px]">
        
        {/* LEFT PANEL - Navigation */}
        <Card className="rounded-3xl border-0 shadow-sm lg:sticky lg:top-8 lg:h-[calc(100vh-4rem)] overflow-hidden">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div style={{ width: '44px', height: '44px' }}>
                  <img 
                    src="/defenciq-logo.png" 
                    alt="DefencIQ Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <div>
                  <CardTitle className="text-xl">DefencIQ<span className="font-normal ml-0.5" style={{ fontSize: '0.6rem', position: 'relative', top: '-0.8em' }}>TM</span></CardTitle>
                  <CardDescription>Essential Eight ML2 Discovery</CardDescription>
                </div>
              </div>
              {/* User Menu */}
              {user && (
                  <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 rounded-full bg-slate-100">
                      <User className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col">
                        <span className="font-medium">{user.name}</span>
                        <span className="text-xs text-slate-500">{user.email}</span>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onBackToDashboard}>
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuItem onClick={onOpenAdmin}>
                          <Settings className="mr-2 h-4 w-4" />
                          Admin Console
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem onClick={() => setShowChangePassword(true)} data-testid="change-password-menu-item">
                      <KeyRound className="mr-2 h-4 w-4" />
                      Change Password
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onLogout} className="text-red-600">
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-5 overflow-hidden">
            {/* Progress */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm text-slate-600">
                <span>Progress</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Section Navigation */}
            <ScrollArea className="h-[calc(100vh-20rem)]">
              <div className="space-y-2 pr-4">
                {sections.map((section, idx) => {
                  const SectionIcon = iconMap[section.icon] || Shield;
                  const score = results.domainScorePercentages?.[section.id];
                  const isActive = idx === currentStep;
                  const hasQuestions = section.questions?.length > 0 && section.id !== 'engagement' && section.id !== 'pain_points';
                  
                  // Essential Eight domains
                  const isE8Domain = [
                    'identity_and_mfa',           // Multi-Factor Authentication
                    'application_control',        // Application Control
                    'patch_management',           // Patch Applications & Patch Operating Systems
                    'privileged_access',          // Restrict Administrative Privileges
                    'office_macros_and_user_hardening', // Configure MS Office Macros & User Application Hardening
                    'backups'                     // Regular Backups
                  ].includes(section.id);
                  
                  // Count unanswered critical questions
                  const criticalUnanswered = section.questions?.filter(q => {
                    if (!q.gating_rule && q.criticality !== 'critical') return false;
                    const answer = answers[q.field_id];
                    return !answer || (!answer.status && answer.answer === undefined);
                  }).length || 0;

                  // Calculate section completion status
                  const sectionQuestions = section.questions || [];
                  let completionState = 'not-started';
                  
                  if (section.id === 'engagement') {
                    // Special handling for engagement section - data is in engagement object
                    const requiredEngagementFields = ['client_name', 'assessor_name', 'engagement_type'];
                    const optionalEngagementFields = ['assessment_date', 'engagement_notes'];
                    const allEngagementFields = [...requiredEngagementFields, ...optionalEngagementFields];
                    
                    const filledRequired = requiredEngagementFields.filter(f => engagement[f]).length;
                    const filledTotal = allEngagementFields.filter(f => engagement[f]).length;
                    
                    if (filledRequired === requiredEngagementFields.length) {
                      completionState = 'completed';
                    } else if (filledTotal > 0) {
                      completionState = 'in-progress';
                    }
                  } else {
                    // Regular sections - check answers object
                    // Filter out:
                    // 1. Conditional questions whose conditions aren't met
                    // 2. Optional questions (required: false) for completion calculation
                    const applicableQuestions = sectionQuestions.filter(q => {
                      // Skip optional questions for completion status
                      if (q.required === false) return false;
                      
                      // Skip conditional questions if condition isn't met
                      if (q.conditionalOn) {
                        const { field, value: conditionValue } = q.conditionalOn;
                        const parentAnswer = answers[field];
                        const parentValue = parentAnswer?.answer || parentAnswer?.status;
                        return parentValue === conditionValue;
                      }
                      return true;
                    });
                    
                    const totalQuestions = applicableQuestions.length;
                    const answeredQuestions = applicableQuestions.filter(q => {
                      const answer = answers[q.field_id];
                      // Check for various answer formats
                      if (!answer) return false;
                      
                      // For multiselect with "Other" selected, check if valid
                      if (q.type === 'multiselect' && answer.isValid === false) {
                        return false; // Not considered answered if "Other" is selected but details not provided
                      }
                      
                      if (answer.status) return true;
                      if (answer.answer !== undefined && answer.answer !== null && answer.answer !== '') {
                        // For arrays (multiselect), check if not empty
                        if (Array.isArray(answer.answer)) return answer.answer.length > 0;
                        return true;
                      }
                      if (answer.value !== undefined && answer.value !== null && answer.value !== '') {
                        if (Array.isArray(answer.value)) return answer.value.length > 0;
                        return true;
                      }
                      return false;
                    }).length;
                    
                    // Also check if any questions have been answered (for in-progress state)
                    const anyAnswered = sectionQuestions.some(q => {
                      const answer = answers[q.field_id];
                      if (!answer) return false;
                      
                      // Don't count invalid multiselect answers
                      if (q.type === 'multiselect' && answer.isValid === false) {
                        return true; // Still counts as "started" even if invalid
                      }
                      
                      if (answer.status) return true;
                      if (answer.answer !== undefined && answer.answer !== null && answer.answer !== '') {
                        if (Array.isArray(answer.answer)) return answer.answer.length > 0;
                        return true;
                      }
                      if (answer.value !== undefined && answer.value !== null && answer.value !== '') {
                        if (Array.isArray(answer.value)) return answer.value.length > 0;
                        return true;
                      }
                      return false;
                    });
                    
                    if (totalQuestions > 0) {
                      if (answeredQuestions === totalQuestions) {
                        completionState = 'completed';
                      } else if (anyAnswered) {
                        completionState = 'in-progress';
                      }
                    } else if (sectionQuestions.length > 0) {
                      // All questions are optional — use all for completion status
                      const allOptionalAnswered = sectionQuestions.every(q => {
                        const answer = answers[q.field_id];
                        if (!answer) return false;
                        if (answer.answer !== undefined && answer.answer !== null && answer.answer !== '') {
                          if (Array.isArray(answer.answer)) return answer.answer.length > 0;
                          return true;
                        }
                        return false;
                      });
                      if (allOptionalAnswered) {
                        completionState = 'completed';
                      } else if (anyAnswered) {
                        completionState = 'in-progress';
                      }
                    }
                  }

                  // Subtle background colors for completion states (matching Gating Controls Summary)
                  const completionBg = {
                    'not-started': 'bg-red-50 border-red-200',
                    'in-progress': 'bg-amber-50 border-amber-200',
                    'completed': 'bg-green-50 border-green-200'
                  };

                  return (
                    <button
                      key={section.id}
                      type="button"
                      onClick={() => onGoToSection(idx)}
                      className={cn(
                        'w-full rounded-2xl border p-3 text-left transition',
                        isActive 
                          ? 'border-slate-900 bg-white shadow-sm' 
                          : cn('hover:brightness-[0.97]', completionBg[completionState])
                      )}
                      data-testid={`nav-section-${section.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="relative">
                          <div className={cn(
                            'rounded-xl p-2',
                            isActive ? 'bg-slate-900 text-white' : 'bg-white text-slate-700'
                          )}>
                            <SectionIcon className="h-4 w-4" />
                          </div>
                          {isE8Domain && (
                            <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded bg-blue-600 text-[8px] font-bold text-white shadow-sm">
                              E8
                            </span>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-slate-900">{section.title}</p>
                            {hasQuestions && typeof score === 'number' && SECTION_CONFIG[section.id]?.scored !== false && (
                              <SectionScorePill score={score} />
                            )}
                          </div>
                          <p className="mt-1 text-xs text-slate-500 line-clamp-1">{section.description}</p>
                          {criticalUnanswered > 0 && (
                            <div className="mt-1 flex items-center gap-1 text-xs text-amber-600">
                              <AlertCircle className="h-3 w-3" />
                              {criticalUnanswered} critical unanswered
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </ScrollArea>

            {/* Actions */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={onSave}
                disabled={isSaving}
                className="flex-1 rounded-xl"
              >
                <Save className="h-4 w-4 mr-1" />
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={onReset}
                className="rounded-xl text-slate-500 hover:text-red-600"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* CENTER PANEL - Questionnaire */}
        <motion.div 
          key={currentSection?.id} 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.2 }}
        >
          <Card className="rounded-3xl border-0 shadow-sm">
            <CardHeader className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-slate-900 p-3 text-white">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-2xl">{currentSection?.title}</CardTitle>
                  <CardDescription className="text-sm">{currentSection?.description}</CardDescription>
                </div>
              </div>
              {currentSection?.questions?.length > 0 && currentSection?.id !== 'engagement' && currentSection?.id !== 'pain_points' && SECTION_CONFIG[currentSection?.id]?.scored !== false && (
                <div className="flex items-center gap-3">
                  <SectionScorePill score={results.domainScorePercentages?.[currentSection.id] || 0} />
                  <p className="text-sm text-slate-500">This score updates live as you answer questions.</p>
                </div>
              )}
            </CardHeader>
            
            <CardContent className="space-y-6">
              {/* Engagement fields (special handling) */}
              {isEngagementSection && currentSection?.questions?.map((question) => (
                <FieldRenderer
                  key={question.field_id}
                  question={question}
                  value={engagement[question.field_id]}
                  onChange={(value) => onUpdateEngagementField(question.field_id, value)}
                  statusOptions={statusOptions}
                  isEngagementField={true}
                />
              ))}

              {/* Client Logo upload (per-engagement override of consultancy default) */}
              {isEngagementSection && (
                <div className="space-y-2" data-testid="client-logo-section">
                  <Label className="text-sm font-medium">
                    Client Logo
                    <span className="ml-2 text-xs text-slate-500 font-normal">Optional - appears on the report cover page</span>
                  </Label>
                  <div className="flex items-center gap-4">
                    {engagement.client_logo_base64 ? (
                      <>
                        <div
                          className="h-16 w-32 rounded-md bg-white flex items-center justify-center p-2 border border-slate-200"
                          data-testid="client-logo-preview"
                        >
                          <img
                            src={engagement.client_logo_base64}
                            alt="Client logo"
                            className="max-h-full max-w-full object-contain"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => onUpdateEngagementField('client_logo_base64', '')}
                          className="text-sm text-rose-600 hover:text-rose-700 font-medium inline-flex items-center gap-1"
                          data-testid="btn-clear-client-logo"
                        >
                          Remove
                        </button>
                      </>
                    ) : (
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 500 * 1024) {
                            toast.error('Logo too large', { description: 'Please use an image under 500KB' });
                            e.target.value = '';
                            return;
                          }
                          const reader = new FileReader();
                          reader.onload = () => onUpdateEngagementField('client_logo_base64', reader.result);
                          reader.readAsDataURL(file);
                        }}
                        className="text-sm cursor-pointer file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:px-3 file:py-1.5 file:font-medium"
                        data-testid="input-client-logo"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Regular questions */}
              {!isEngagementSection && currentSection?.questions?.map((question) => {
                // Handle conditional questions
                if (question.conditionalOn) {
                  const { field, value: conditionValue } = question.conditionalOn;
                  const parentAnswer = answers[field];
                  const parentValue = parentAnswer?.answer || parentAnswer?.status;
                  if (parentValue !== conditionValue) {
                    return null; // Hide question if condition not met
                  }
                }
                
                // Determine if question is answered
                const answer = answers[question.field_id];
                const isAnswered = (() => {
                  if (!answer) return false;
                  // For multiselect with "Other" selected, check if valid
                  if (question.type === 'multiselect' && answer.isValid === false) return false;
                  if (answer.status) return true;
                  if (answer.answer !== undefined && answer.answer !== null && answer.answer !== '') {
                    if (Array.isArray(answer.answer)) return answer.answer.length > 0;
                    return true;
                  }
                  if (answer.value !== undefined && answer.value !== null && answer.value !== '') {
                    if (Array.isArray(answer.value)) return answer.value.length > 0;
                    return true;
                  }
                  return false;
                })();
                
                return (
                  <div 
                    key={question.field_id} 
                    className={cn(
                      'relative rounded-2xl border p-5 shadow-md transition-all duration-200',
                      // Base styling - answered vs unanswered
                      isAnswered 
                        ? 'bg-white border-slate-200' 
                        : 'bg-slate-50/80 border-slate-200/80',
                      // Gating control override
                      question.gating_rule && 'border-amber-200 bg-amber-50/50',
                      question.gating_rule && isAnswered && 'bg-amber-50/30',
                      // Conditional question indent
                      question.conditionalOn && 'ml-4 border-l-4 border-l-slate-300'
                    )}
                    data-testid={`question-${question.field_id}`}
                  >
                    {/* Answer status indicator dot */}
                    <div 
                      className={cn(
                        'absolute top-3 right-3 h-2.5 w-2.5 rounded-full shadow-sm',
                        isAnswered ? 'bg-green-500' : 'bg-red-400'
                      )}
                      title={isAnswered ? 'Answered' : 'Not answered'}
                    />

                    {/* Carried-over-from-previous-assessment pill */}
                    {prefilledIds && prefilledIds.has(question.field_id) && (
                      <div
                        className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-800"
                        title={`Answer carried over from previous assessment${prefillSourceDate ? ` (${prefillSourceDate})` : ''}. Edit to clear the tag.`}
                        data-testid={`prefill-pill-${question.field_id}`}
                      >
                        <RotateCcw className="h-3 w-3" strokeWidth={2.25} />
                        <span>
                          Carried over{prefillSourceDate ? ` from ${prefillSourceDate}` : ''}
                        </span>
                      </div>
                    )}

                    <FieldRenderer
                      question={question}
                      value={answers[question.field_id]}
                      onChange={(payload) => onUpdateSectionField(currentSection.id, question.field_id, payload)}
                      statusOptions={statusOptions}
                    />
                    
                    {/* Question metadata badges */}
                    {(question.criticality || question.gating_rule || question.recommended_components?.length) && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {question.gating_rule && (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Gating control
                          </Badge>
                        )}
                        {question.criticality === 'critical' && !question.gating_rule && (
                          <Badge variant="secondary" className="bg-red-100 text-red-700">Critical</Badge>
                        )}
                        {question.criticality === 'high' && (
                          <Badge variant="secondary" className="bg-amber-100 text-amber-700">High priority</Badge>
                        )}
                        {question.recommended_components?.slice(0, 3).map((component) => (
                          <Badge key={component} variant="outline" className="text-xs">
                            {COMPONENT_LABELS[component] || component}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}

              <Separator />

              {/* Assessor review gate - only when prefilled answers remain */}
              {prefilledIds && prefilledIds.size > 0 && currentStep === totalSteps - 1 && (
                <div
                  className="rounded-2xl border border-amber-200 bg-amber-50 p-4"
                  data-testid="prefill-review-gate"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-amber-100 border border-amber-300 shrink-0">
                      <AlertTriangle className="h-3 w-3 text-amber-700" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-amber-900">
                        {prefilledIds.size} pre-filled answer{prefilledIds.size === 1 ? '' : 's'} still carried over from {prefillSourceDate || 'a previous assessment'}
                      </div>
                      <p className="mt-1 text-xs text-amber-800">
                        Please confirm you have reviewed each carried-over answer and it still reflects the current control state before generating the report.
                      </p>
                      <label className="mt-3 flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!prefillReviewed}
                          onChange={(e) => onSetPrefillReviewed?.(e.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-amber-300 text-amber-700 focus:ring-amber-500"
                          data-testid="prefill-review-checkbox"
                        />
                        <span className="text-xs text-amber-900">
                          I have reviewed each pre-filled answer and confirmed it still reflects the current control state.
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Button 
                  variant="outline" 
                  onClick={onGoBack} 
                  disabled={currentStep === 0} 
                  className="rounded-2xl"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <div className="flex gap-3">
                  {currentStep < totalSteps - 1 ? (
                    <Button onClick={onGoNext} className="rounded-2xl">
                      Next <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  ) : (
                    <Button 
                      onClick={onSubmit} 
                      disabled={!allSectionsCompleted || (prefilledIds && prefilledIds.size > 0 && !prefillReviewed)}
                      className={cn(
                        "rounded-2xl",
                        allSectionsCompleted && !(prefilledIds && prefilledIds.size > 0 && !prefillReviewed)
                          ? "bg-green-600 hover:bg-green-700" 
                          : "bg-slate-300 cursor-not-allowed"
                      )}
                      title={
                        !allSectionsCompleted
                          ? "Complete all sections before generating output"
                          : (prefilledIds && prefilledIds.size > 0 && !prefillReviewed)
                            ? "Confirm you have reviewed the pre-filled answers"
                            : ""
                      }
                      data-testid="workbench-submit-button"
                    >
                      <CheckCircle2 className="mr-2 h-4 w-4" /> Generate Readiness Output
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* RIGHT PANEL - Readiness Snapshot */}
        <Card className="rounded-3xl border-0 shadow-sm lg:sticky lg:top-8 lg:h-[calc(100vh-4rem)] overflow-hidden">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">Readiness Snapshot</CardTitle>
            <CardDescription>Live scoring, recommended stack, and likely service path.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5 overflow-y-auto max-h-[calc(100vh-12rem)]">
            {/* Enhanced Final Score */}
            <div className={cn(
              "rounded-2xl p-5 text-white",
              results.enhanced?.scoreBand?.color === 'emerald' && 'bg-emerald-600',
              results.enhanced?.scoreBand?.color === 'yellow' && 'bg-yellow-500',
              results.enhanced?.scoreBand?.color === 'orange' && 'bg-orange-500',
              results.enhanced?.scoreBand?.color === 'red' && 'bg-red-600',
              results.enhanced?.scoreBand?.color === 'rose' && 'bg-rose-700',
              !results.enhanced?.scoreBand?.color && 'bg-slate-900'
            )}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Weighted ML2 Score</p>
                  <p className="mt-1 text-4xl font-bold">{results.enhanced?.finalScore || 0}%</p>
                </div>
                <Badge className="rounded-full bg-white/20 px-3 py-1 text-white">
                  {results.enhanced?.scoreBand?.label || 'Calculating...'}
                </Badge>
              </div>
              {/* Score breakdown */}
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-white/10 p-2">
                  <p className="text-xs opacity-80">CES</p>
                  <p className="text-lg font-semibold">{results.enhanced?.groupScores?.CES || 0}%</p>
                </div>
                <div className="rounded-lg bg-white/10 p-2">
                  <p className="text-xs opacity-80">PGS</p>
                  <p className="text-lg font-semibold">{results.enhanced?.groupScores?.PGS || 0}%</p>
                </div>
                <div className="rounded-lg bg-white/10 p-2">
                  <p className="text-xs opacity-80">CMS</p>
                  <p className="text-lg font-semibold">{results.enhanced?.groupScores?.CMS || 0}%</p>
                </div>
              </div>
              {/* Multipliers info */}
              {(results.enhanced?.multipliers?.policyEnforcement < 1 || 
                results.enhanced?.multipliers?.evidence < 1 || 
                results.enhanced?.multipliers?.confidence < 1) && (
                <div className="mt-3 text-xs opacity-75 border-t border-white/20 pt-2">
                  <span>Multipliers applied: </span>
                  {results.enhanced?.multipliers?.policyEnforcement < 1 && (
                    <span className="mr-2">Policy ×{results.enhanced.multipliers.policyEnforcement.toFixed(2)}</span>
                  )}
                  {results.enhanced?.multipliers?.evidence < 1 && (
                    <span className="mr-2">Evidence ×{results.enhanced.multipliers.evidence.toFixed(2)}</span>
                  )}
                  {results.enhanced?.multipliers?.confidence < 1 && (
                    <span>Confidence ×{results.enhanced.multipliers.confidence.toFixed(2)}</span>
                  )}
                </div>
              )}
            </div>

            {/* ===== Evidence Readiness Card (informational, click to jump to section) ===== */}
            {(() => {
              const er = results.evidenceReadiness;
              const bandBg = !er?.assessed ? 'bg-slate-100 text-slate-600 border-slate-200'
                : er.band?.color === 'emerald' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                : er.band?.color === 'sky' ? 'bg-sky-50 text-sky-700 border-sky-200'
                : er.band?.color === 'amber' ? 'bg-amber-50 text-amber-700 border-amber-200'
                : 'bg-rose-50 text-rose-700 border-rose-200';
              return (
                <button
                  type="button"
                  onClick={() => onGoToSectionById?.('evidence_readiness')}
                  className="w-full text-left rounded-2xl border border-slate-200 bg-white p-5 hover:border-indigo-300 hover:shadow-sm transition-all"
                  data-testid="evidence-readiness-card"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Evidence Readiness</p>
                      <p className="mt-1 text-3xl font-bold text-slate-900" data-testid="evidence-readiness-score">
                        {er?.assessed ? `${er.overall_percent}%` : 'Not Assessed'}
                      </p>
                      {er?.assessed && (
                        <p className="text-xs text-slate-500 mt-0.5">Reported presence of supporting evidence (Discovery-level only)</p>
                      )}
                    </div>
                    <span className={cn('inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full border', bandBg)} data-testid="evidence-readiness-band">
                      {er?.assessed ? er.band.label.toUpperCase() : 'NOT ASSESSED'}
                    </span>
                  </div>
                  {er?.assessed && (
                    <>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-2">
                          <p className="text-[10px] text-emerald-700 uppercase tracking-wide">Available</p>
                          <p className="text-lg font-semibold text-emerald-700">{er.counts.available}</p>
                        </div>
                        <div className="rounded-lg bg-amber-50 border border-amber-100 p-2">
                          <p className="text-[10px] text-amber-700 uppercase tracking-wide">Partial</p>
                          <p className="text-lg font-semibold text-amber-700">{er.counts.partial}</p>
                        </div>
                        <div className="rounded-lg bg-rose-50 border border-rose-100 p-2">
                          <p className="text-[10px] text-rose-700 uppercase tracking-wide">Missing</p>
                          <p className="text-lg font-semibold text-rose-700">{er.counts.missing}</p>
                        </div>
                      </div>
                      <div className="mt-3 space-y-1">
                        {er.per_domain.map(d => (
                          <div key={d.domain} className="flex items-center justify-between text-xs" data-testid={`evidence-domain-${d.domain}`}>
                            <span className="text-slate-600">{d.label}</span>
                            <span className={cn(
                              'font-medium',
                              d.percent === null ? 'text-slate-400'
                                : d.band?.color === 'emerald' ? 'text-emerald-700'
                                : d.band?.color === 'sky' ? 'text-sky-700'
                                : d.band?.color === 'amber' ? 'text-amber-700'
                                : 'text-rose-700'
                            )}>
                              {d.percent === null ? '—' : `${d.percent}%`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </button>
              );
            })()}

            {/* Audit Failure Likelihood - Priority Alert Banner for CRITICAL/HIGH */}
            {(results.enhanced?.auditFailureLikelihood === 'CRITICAL' || results.enhanced?.auditFailureLikelihood === 'HIGH') && (
              <div className={cn(
                "rounded-2xl p-4 border-2",
                results.enhanced?.auditFailureLikelihood === 'CRITICAL' && 'bg-red-50 border-red-400',
                results.enhanced?.auditFailureLikelihood === 'HIGH' && 'bg-orange-50 border-orange-400'
              )}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className={cn(
                    "h-5 w-5",
                    results.enhanced?.auditFailureLikelihood === 'CRITICAL' && 'text-red-600',
                    results.enhanced?.auditFailureLikelihood === 'HIGH' && 'text-orange-600'
                  )} />
                  <Badge className={cn(
                    "font-bold",
                    results.enhanced?.auditFailureLikelihood === 'CRITICAL' && 'bg-red-600 text-white',
                    results.enhanced?.auditFailureLikelihood === 'HIGH' && 'bg-orange-500 text-white'
                  )}>
                    {results.enhanced?.auditFailureLikelihood} AUDIT RISK
                  </Badge>
                </div>
                <p className={cn(
                  "text-sm mb-3",
                  results.enhanced?.auditFailureLikelihood === 'CRITICAL' && 'text-red-800',
                  results.enhanced?.auditFailureLikelihood === 'HIGH' && 'text-orange-800'
                )}>
                  {results.enhanced?.auditFailureMessage}
                </p>
                {results.enhanced?.auditFailureReasons?.length > 0 && (
                  <ul className="text-xs space-y-1">
                    {results.enhanced.auditFailureReasons.map((reason, idx) => (
                      <li key={idx} className={cn(
                        "flex items-start gap-1",
                        results.enhanced?.auditFailureLikelihood === 'CRITICAL' && 'text-red-700',
                        results.enhanced?.auditFailureLikelihood === 'HIGH' && 'text-orange-700'
                      )}>
                        <span>•</span>
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Audit Failure Likelihood - Standard Display for MODERATE/LOW */}
            {(results.enhanced?.auditFailureLikelihood === 'MODERATE' || results.enhanced?.auditFailureLikelihood === 'LOW') && (
              <div className={cn(
                "rounded-2xl p-4 border",
                results.enhanced?.auditFailureLikelihood === 'MODERATE' && 'bg-yellow-50 border-yellow-300',
                results.enhanced?.auditFailureLikelihood === 'LOW' && 'bg-green-50 border-green-300'
              )}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-600">Audit Failure Risk</span>
                  <Badge className={cn(
                    results.enhanced?.auditFailureLikelihood === 'MODERATE' && 'bg-yellow-500 text-white',
                    results.enhanced?.auditFailureLikelihood === 'LOW' && 'bg-green-500 text-white'
                  )}>
                    {results.enhanced?.auditFailureLikelihood}
                  </Badge>
                </div>
                <p className={cn(
                  "text-xs mb-2",
                  results.enhanced?.auditFailureLikelihood === 'MODERATE' && 'text-yellow-800',
                  results.enhanced?.auditFailureLikelihood === 'LOW' && 'text-green-800'
                )}>
                  {results.enhanced?.auditFailureMessage}
                </p>
                {results.enhanced?.auditFailureReasons?.length > 0 && (
                  <ul className="text-xs space-y-0.5">
                    {results.enhanced.auditFailureReasons.map((reason, idx) => (
                      <li key={idx} className="text-slate-600 flex items-start gap-1">
                        <span>•</span>
                        <span>{reason}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* FOCI Gate Status */}
            {results.fociGate && (
              <div className={cn(
                "rounded-2xl p-4 border",
                results.fociGate.status === 'fail' && 'bg-red-50 border-red-400',
                results.fociGate.status === 'review_required' && 'bg-amber-50 border-amber-300',
                results.fociGate.status === 'pass' && 'bg-green-50 border-green-300'
              )} data-testid="foci-gate-status">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-600">FOCI Assessment</span>
                  <Badge className={cn(
                    results.fociGate.status === 'fail' && 'bg-red-600 text-white',
                    results.fociGate.status === 'review_required' && 'bg-amber-500 text-white',
                    results.fociGate.status === 'pass' && 'bg-green-500 text-white'
                  )}>
                    {results.fociGate.status === 'fail' ? 'FAIL' :
                     results.fociGate.status === 'review_required' ? 'REVIEW REQUIRED' : 'PASS'}
                  </Badge>
                </div>
                <p className="text-xs text-slate-600">{results.fociGate.reason}</p>
                {results.fociGate.status === 'fail' && (
                  <p className="text-xs text-red-700 mt-1 font-medium">Readiness capped at Partially Ready</p>
                )}
                {results.fociGate.status === 'review_required' && (
                  <p className="text-xs text-amber-700 mt-1 font-medium">Readiness capped at Substantially Ready</p>
                )}
              </div>
            )}

            {/* Flags / Alerts */}
            {results.enhanced?.flags?.length > 0 && (
              <div className="space-y-2">
                {results.enhanced.flags.map((flag) => (
                  <div 
                    key={flag.id}
                    className={cn(
                      "rounded-xl p-3 border-l-4",
                      flag.severity === 'critical' && 'bg-red-50 border-l-red-500',
                      flag.severity === 'high' && 'bg-orange-50 border-l-orange-500',
                      flag.severity === 'medium' && 'bg-yellow-50 border-l-yellow-500'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle className={cn(
                        "h-4 w-4",
                        flag.severity === 'critical' && 'text-red-500',
                        flag.severity === 'high' && 'text-orange-500',
                        flag.severity === 'medium' && 'text-yellow-600'
                      )} />
                      <span className={cn(
                        "text-sm font-semibold",
                        flag.severity === 'critical' && 'text-red-700',
                        flag.severity === 'high' && 'text-orange-700',
                        flag.severity === 'medium' && 'text-yellow-700'
                      )}>
                        {flag.label}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 mt-1 ml-6">{flag.description}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Section Group Breakdown */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">Score Components</h3>
              <div className="space-y-2">
                {Object.entries(SECTION_GROUPS).map(([key, group]) => {
                  const score = results.enhanced?.groupScores?.[key] || 0;
                  const maturityLabel = score >= 85 ? 'Strong' : score >= 65 ? 'Moderate' : score >= 40 ? 'Developing' : 'Poor';
                  const maturityColor = score >= 85 ? 'text-green-600' : score >= 65 ? 'text-yellow-600' : score >= 40 ? 'text-orange-600' : 'text-red-600';
                  
                  return (
                    <div key={key} className="rounded-xl border bg-slate-50 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="text-sm font-medium text-slate-700">{group.shortName}</span>
                          <span className="text-xs text-slate-500 ml-2">({Math.round(group.weight * 100)}% weight)</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-semibold">{score}%</span>
                          <span className={cn("text-xs ml-2", maturityColor)}>{maturityLabel}</span>
                        </div>
                      </div>
                      <Progress value={score} className="h-1.5" />
                      <p className="text-xs text-slate-500 mt-1">{group.name}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Discovery Summary */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">Discovery Summary</h3>
              <div className="rounded-2xl border bg-slate-50 p-4 space-y-4">
                {/* Environment Type (Descriptive) */}
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Environment Type</span>
                    <Badge variant="outline" className="bg-slate-100">
                      {results.environmentTypeInfo?.label || 'Unknown'}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {results.environmentTypeInfo?.description}
                  </p>
                </div>
                
                <Separator />
                
                {/* Service Tier (Prescriptive) */}
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Recommended Service</span>
                    <Badge className="bg-slate-900 text-white">
                      {results.serviceTierInfo?.label || 'Unknown'}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {results.serviceTierInfo?.description}
                  </p>
                </div>
                
                {results.missingLayers?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <span className="text-xs text-slate-500">Stack layers needing attention:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {results.missingLayers.map(layer => (
                          <Badge key={layer} variant="secondary" className="text-xs bg-amber-100 text-amber-700">
                            {layer}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Technology Stack Clarity Card */}
            {results.stackAnalysis && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <Layers className="h-4 w-4 text-blue-500" />
                  Technology Stack Clarity
                </h3>
                <div className="rounded-2xl border bg-slate-50 p-4 space-y-3" data-testid="stack-clarity-card">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl bg-green-50 border border-green-200 p-2.5 text-center">
                      <p className="text-lg font-bold text-green-700">{results.stackAnalysis.summary.implemented}</p>
                      <p className="text-[10px] text-green-600 font-medium">Implemented</p>
                    </div>
                    <div className="rounded-xl bg-orange-50 border border-orange-200 p-2.5 text-center">
                      <p className="text-lg font-bold text-orange-700">
                        {results.stackAnalysis.summary.needs_improvement + results.stackAnalysis.summary.partial}
                      </p>
                      <p className="text-[10px] text-orange-600 font-medium">Needs Improvement</p>
                    </div>
                    <div className="rounded-xl bg-red-50 border border-red-200 p-2.5 text-center">
                      <p className="text-lg font-bold text-red-700">{results.stackAnalysis.summary.missing}</p>
                      <p className="text-[10px] text-red-600 font-medium">Missing</p>
                    </div>
                    <div className="rounded-xl bg-slate-100 border border-slate-200 p-2.5 text-center">
                      <p className="text-lg font-bold text-slate-500">{results.stackAnalysis.summary.planned}</p>
                      <p className="text-[10px] text-slate-400 font-medium">Planned</p>
                    </div>
                  </div>
                  {results.stackAnalysis.stack_alignment_score !== undefined && (
                    <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                      <span className="text-xs text-slate-500">Stack Alignment</span>
                      <span className={cn(
                        "text-sm font-semibold",
                        results.stackAnalysis.stack_alignment_score >= 70 ? 'text-green-600' :
                        results.stackAnalysis.stack_alignment_score >= 40 ? 'text-amber-600' : 'text-red-600'
                      )}>
                        {results.stackAnalysis.stack_alignment_score}%
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Navigation to Gating Failures */}
            {results.gatingFailures?.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500" />
                  Gating Failures ({results.gatingFailures.length})
                </h3>
                <ScrollArea className="h-[280px] pr-3">
                  <div className="space-y-2">
                    {results.gatingFailures.map((control) => (
                      <button
                        key={control.field_id}
                        onClick={() => onGoToSectionById(control.section_id)}
                        className="w-full rounded-xl border border-red-200 bg-red-50 p-3 text-left hover:bg-red-100 transition"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm text-slate-900 line-clamp-2">{control.prompt}</p>
                          <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{SECTION_CONFIG[control.section_id]?.title}</p>
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}

            {/* Service Recommendation */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">Service recommendation</h3>
              <div className="grid gap-2 text-sm text-slate-600">
                <div className="flex items-center justify-between rounded-xl border p-3">
                  <span>Greenfield candidate</span>
                  <span className={cn('font-medium', results.greenfieldCandidate ? 'text-green-600' : 'text-slate-900')}>
                    {results.greenfieldCandidate ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-xl border p-3">
                  <span>Managed uplift candidate</span>
                  <span className={cn('font-medium', results.managedUpliftCandidate ? 'text-blue-600' : 'text-slate-900')}>
                    {results.managedUpliftCandidate ? 'Yes' : 'No'}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-xl border p-3">
                  <span>Gating failures</span>
                  <span className={cn('font-medium', results.gatingFailures?.length > 0 ? 'text-red-600' : 'text-green-600')}>
                    {results.gatingFailures?.length || 0}
                  </span>
                </div>
              </div>
            </div>

            {/* Recommended Stack */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">Recommended stack</h3>
              {Object.entries(results.stackByLayer || {}).map(([layer, layerData]) => (
                <div key={layer} className="rounded-2xl border p-3">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {LAYER_LABELS[layer] || layer.replace(/_/g, ' ')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {layerData?.components?.length > 0 ? (
                      layerData.components.map((comp) => (
                        <Badge key={comp.id} variant="secondary" className="text-xs">
                          {comp.label}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-slate-400">No components needed</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Top 5 Discussion Points */}
            {results.discussionPoints?.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900">Top Discussion Points</h3>
                <div className="space-y-2">
                  {results.discussionPoints.map((point, idx) => (
                    <div
                      key={idx}
                      className="rounded-xl border p-3 cursor-pointer transition-colors hover:bg-slate-50 hover:border-slate-300"
                      data-testid={`discussion-point-${idx}`}
                      onClick={() => {
                        onGoToSectionById(point.sectionId);
                        setTimeout(() => {
                          const el = document.querySelector(`[data-testid="question-${point.fieldId}"]`);
                          if (el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            el.classList.add('ring-2', 'ring-blue-400', 'ring-offset-2');
                            setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400', 'ring-offset-2'), 2000);
                          }
                        }, 150);
                      }}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-xs font-medium text-slate-900">{point.domain}</p>
                        <Badge className={cn(
                          'text-xs',
                          point.priority === 'critical' && 'bg-red-100 text-red-700',
                          point.priority === 'high' && 'bg-amber-100 text-amber-700',
                          point.priority === 'medium' && 'bg-blue-100 text-blue-700',
                          point.priority === 'low' && 'bg-slate-100 text-slate-700'
                        )}>
                          {point.priority}
                        </Badge>
                      </div>
                      <p className="mt-1 text-sm text-slate-600 line-clamp-2">{point.topic}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Export Button */}
            <Button 
              onClick={handleExport}
              variant="outline" 
              className="w-full rounded-2xl"
            >
              <Download className="mr-2 h-4 w-4" />
              Export JSON Report
            </Button>

            {/* Export to Accelerator */}
            <Button
              onClick={handleExportAccelerator}
              className="w-full rounded-2xl"
              data-testid="export-accelerator-btn"
            >
              <ArrowUpRight className="mr-2 h-4 w-4" />
              Export to Accelerator
            </Button>

            {/* View Full Report Button */}
            <Button 
              onClick={onViewReport}
              className="w-full rounded-2xl bg-slate-900 hover:bg-slate-800"
              data-testid="view-full-report-btn"
            >
              <FileCheck className="mr-2 h-4 w-4" />
              View Full Report
            </Button>

            {/* Submitted state */}
            {submitted && (
              <div className="rounded-2xl border border-green-200 bg-green-50 p-4 text-sm text-green-800">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="font-semibold">Assessment Complete</span>
                </div>
                Ready to generate final report and export.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
    <ChangePasswordDialog open={showChangePassword} onClose={() => setShowChangePassword(false)} />
    </>
  );
}
